import clsx from 'clsx'
import isString from 'lodash/isString'
import React, { FC, forwardRef, MouseEventHandler, ReactNode, useCallback } from 'react'

import { FormElementProps } from '@Components/FormElements/FormElementProps'
import { useFormElementState } from '@Components/FormElements/useFormElementState'

import styles from './Checkbox.module.scss'

export type CheckboxProps = Omit<React.JSX.IntrinsicElements['input'], 'type' | 'size' | 'id'>
& Pick<FormElementProps, 'error' | 'wrapperClassName'>
& {
  disabled?: boolean
  id: string
  indicatorClassName?: string
  label: string | ReactNode
  labelClassName?: string
  rounded?: boolean
  secondaryLabel?: string
  withoutEmptySpan?: boolean
}

export const Checkbox: FC<CheckboxProps> = forwardRef(
  (
    {
      'aria-label': ariaLabel,
      children,
      disabled,
      error,
      id,
      indicatorClassName = '',
      label,
      labelClassName = '',
      rounded,
      secondaryLabel,
      title,
      withoutEmptySpan = false,
      wrapperClassName = '',
      ...props
    }: CheckboxProps,
    ref,
  ) => {
    const {
      formActive,
      ...state
    } = useFormElementState<'input'>({
      ...props,
      disabled,
    })
    const { className } = props

    // A default handler to blur the checkbox after clicking it. It's purely for 𝕒𝕖𝕤𝕥𝕙𝕖𝕥𝕚𝕔 reasons.
    const blurActiveElement: MouseEventHandler<HTMLInputElement> = useCallback(({ currentTarget }) => currentTarget.blur(), [])

    const hideEmptySpan = withoutEmptySpan && !children && !label && !secondaryLabel && !error

    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <label
        aria-label={ariaLabel || (isString(label) ? label : '')}
        className={clsx(
          styles.checkbox,
          disabled && styles.disabled,
          rounded && styles.rounded,
          withoutEmptySpan && styles.withoutEmptySpan,
          wrapperClassName && styles.wrapper,
          labelClassName,
        )}
        htmlFor={id}
        onClick={(e) => {
          if (withoutEmptySpan) {
            e.stopPropagation()
          }
        }}
        title={title}
      >
        <input
          aria-errormessage={error}
          aria-invalid={Boolean(error) || undefined}
          autoComplete="off"
          id={id}
          onClick={blurActiveElement}
          ref={ref}
          type="checkbox"
          {...props}
          {...state}
        />
        <span
          aria-hidden
          className={clsx(
            styles.indicator,
            rounded && styles.rounded,
            indicatorClassName,
          )}
        >
          <div className={styles.checkedBox} />
        </span>
        {!hideEmptySpan && (
          <span className={clsx(className, styles.checkboxTextLabel)}>
            <span className={styles.label}>{children || label}</span>
            {secondaryLabel && <span className={styles.counter}>{secondaryLabel}</span>}
            {error && <span aria-hidden className={styles.errorMessage}>{error}</span>}
          </span>
        )}
      </label>
    )
  },
)
